import {
    forwardRef,
    memo,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import ReactPlayer from 'react-player/file';
import mpegts from 'mpegts.js';
import { withTranslation } from 'react-i18next';
import { useFocusVisible } from 'react-aria';
import _ from 'lodash';

import useFullscreen from '../../hooks/useFullscreen';
import useKeyboardZoomControls from '../../hooks/useKeyboardZoomControls';

import { ReactComponent as PlayIcon } from '../../assets/images/icons/play.svg';
import { ReactComponent as PauseIcon } from '../../assets/images/icons/pause.svg';
import { ReactComponent as AudioOnIcon } from '../../assets/images/icons/audio-on.svg';
import { ReactComponent as AudioOffIcon } from '../../assets/images/icons/audio-off.svg';
import { ReactComponent as FullscreenIcon } from '../../assets/images/icons/fullscreen.svg';
import { ReactComponent as ShrinkIcon } from '../../assets/images/icons/shrink.svg';
import { ReactComponent as StandardViewIcon } from '../../assets/images/icons/rectangle.svg';
import { ReactComponent as DewarpedViewIcon } from '../../assets/images/icons/panorama.svg';

import translationKeys from '../../translations/keys';

import { isUsingMouse, isUsingTouchscreen } from '../../utils/pointer';
import { getMediaSnapshot, shouldStretchToContainer } from '../../utils/video';
import {
    ORIENTATION_CEILING,
    ORIENTATION_FLOOR,
    ORIENTATION_WALL,
    VIEW_QUADRANTS,
    VIEW_SINGLE,
    VIEW_TRIPLE,
} from '../../utils/dewarping';

import Span from '../span';
import { Input } from '../form';
import {
    ZoomControls,
    ZoomableContent,
    ZoomableWrapper,
} from '../zoomable-wrapper';
import {
    FullscreenButtonContainer,
    ModeButtonContainer,
    PlayButtonContainer,
    ScrubBarContainer,
    VideoControlBar,
    VideoControlButton,
    VideoKeyboardAndTouchInterceptor,
    VideoPlayerContainer,
    VideoPlayerWithControlsContainer,
    VolumeButtonContainer,
} from './VideoPlayer.styles';

import Dewarper from './dewarper';
import WebRtcPlayer from './WebRtcPlayer';
import {
    trackVideoPlayerCreated,
    trackVideoPlayerDestroyed,
} from '../../utils/videoPlayerStats';

// mpegts.js logs a lot - turn that off here unless requested in session storage
mpegts.LoggingControl.enableAll = sessionStorage.mpegtsLogging === 'true';

const SCRUB_BAR_STEP = 0.1;

// Video player without controls
const VideoPlayer = memo(
    forwardRef(
        (
            {
                url,
                volume,
                playing = false,
                muted = false,
                playsInline = false,
                playbackRate = 1,
                width = '100%',
                height = '100%',
                dewarpView,
                fisheyeOrientation,
                selectedDewarpCamera,
                transport,
                onReady,
                onPlay,
                onPause,
                onBuffer,
                onBufferEnd,
                onError,
                onLoadedData,
                onProgress,
                onDuration,
                style,
                config,
                overlayContent,
                stretchToContainer = true,
                ...props
            },
            ref
        ) => {
            const containerRef = useRef();
            const playerRef = useRef();

            const dewarperRef = useRef();

            // Expose fullscreen function through ref
            const [toggleFullscreen] = useFullscreen(containerRef);

            // Show darkening animation when this is true
            const [isSnapshotTaken, setIsSnapshotTaken] = useState(false);
            // Reset snapshot state to clear darkened overlay
            useEffect(() => {
                if (isSnapshotTaken) {
                    const timeout = setTimeout(
                        () => setIsSnapshotTaken(false),
                        200
                    );
                    return () => {
                        clearTimeout(timeout);
                    };
                }
            }, [isSnapshotTaken]);

            // Stretch to fill the surrounding 16:9 container when the source reports a ~8:9 aspect ratio (some cameras send anamorphic video this way).
            const [stretchDetected, setStretchDetected] = useState(false);

            useEffect(() => {
                setStretchDetected(false);
            }, [url]);

            const existingOnLoadedMetadata =
                config?.file?.attributes?.onLoadedMetadata;
            const handleLoadedMetadata = useCallback(
                (event) => {
                    if (stretchToContainer) {
                        const video = event?.target;
                        const aspectRatio =
                            video?.videoWidth && video?.videoHeight
                                ? video.videoWidth / video.videoHeight
                                : null;
                        setStretchDetected(
                            shouldStretchToContainer(aspectRatio)
                        );
                    }
                    existingOnLoadedMetadata?.(event);
                },
                [stretchToContainer, existingOnLoadedMetadata]
            );

            useImperativeHandle(
                ref,
                () => {
                    const inner = playerRef.current;
                    return {
                        container: containerRef.current,
                        player: inner,
                        reactPlayer:
                            inner.isMpegts || inner.isWebRtc ? null : inner,
                        mpegts: inner.isMpegts ? inner : null,
                        webrtc: inner.isWebRtc ? inner : null,
                        getInternalPlayer: inner.getInternalPlayer,
                        getDewarpedCanvas: () => dewarperRef.current?.canvas,
                        dewarpZoom: (...args) =>
                            dewarperRef.current?.zoom(...args),
                        dewarpMoveX: (...args) =>
                            dewarperRef.current?.moveX(...args),
                        dewarpMoveY: (...args) =>
                            dewarperRef.current?.moveY(...args),
                        toggleFullscreen,
                        hasAudio: () => {
                            return (
                                !!playerRef.current.getInternalPlayer() &&
                                doesVideoHaveAudio(
                                    playerRef.current.getInternalPlayer()
                                )
                            );
                        },
                        takeSnapshot: (zoomTransformState) => {
                            setIsSnapshotTaken(true);
                            if (dewarperRef.current) {
                                return dewarperRef.current.canvas;
                            } else {
                                const canvas = getMediaSnapshot(
                                    playerRef.current.getInternalPlayer(),
                                    containerRef.current,
                                    zoomTransformState
                                );
                                return canvas;
                            }
                        },
                        showSnapshotAnimation: () => {
                            setIsSnapshotTaken(true);
                        },
                    };
                },
                [toggleFullscreen]
            );

            // Player selection:
            //   - Explicit `transport="webrtc"` uses the WHEP/WebRTC player
            //   - Our video server URLs (/stream/, /transcode/) require mpegts.js
            //   - Otherwise fall back to ReactPlayer (HLS)
            const Player =
                transport === 'webrtc'
                    ? WebRtcPlayer
                    : url.match(/\/(stream|transcode)\//)
                    ? MpegTsPlayer
                    : ReactPlayer;

            return (
                <VideoPlayerContainer
                    ref={containerRef}
                    style={_.merge(style ?? {}, { width, height })}
                    $overlay={isSnapshotTaken}
                    $stretchToContainer={stretchDetected}
                >
                    <Player
                        key={url}
                        ref={playerRef}
                        url={url}
                        width="100%"
                        height="100%"
                        playing={playing}
                        volume={volume}
                        muted={muted}
                        playsinline={playsInline}
                        playbackRate={playbackRate}
                        controls={false} // We use our own controls
                        onReady={onReady}
                        onPlay={onPlay}
                        onPause={onPause}
                        onBuffer={onBuffer}
                        onBufferEnd={onBufferEnd}
                        onError={onError}
                        onProgress={onProgress}
                        onDuration={onDuration}
                        style={
                            dewarpView
                                ? {
                                      opacity: 0,
                                      position: 'absolute',
                                      zIndex: -500,
                                  }
                                : undefined
                        }
                        {...props}
                        // Need crossorigin="anonymous" to avoid cors errors when using <canvas>
                        config={_.merge(
                            {},
                            {
                                file: {
                                    attributes: {
                                        crossOrigin: 'anonymous',
                                        onLoadedData,
                                        onLoadedMetadata: handleLoadedMetadata,
                                    },
                                },
                            },
                            config ?? {},
                            {
                                file: {
                                    attributes: {
                                        onLoadedMetadata: handleLoadedMetadata,
                                    },
                                },
                            }
                        )}
                    />

                    {dewarpView && (
                        <Dewarper
                            ref={dewarperRef}
                            reactPlayerRef={playerRef}
                            view={dewarpView}
                            orientation={fisheyeOrientation}
                            selectedCamera={selectedDewarpCamera}
                        />
                    )}
                    {overlayContent}
                </VideoPlayerContainer>
            );
        }
    )
);

VideoPlayer.propTypes = {
    /** URL of video. */
    url: PropTypes.string.isRequired,
    /** Set to `true` or `false` to pause or play the video. */
    playing: PropTypes.bool,
    /** Set the volume between 0 and 1. */
    volume: PropTypes.number,
    /** Mute the player. */
    muted: PropTypes.bool,
    /** Applies the `playsinline` attribute where supported. */
    playsInline: PropTypes.bool,
    /** Set the playback rate. */
    playbackRate: PropTypes.number,
    /** Show video controls. */
    controls: PropTypes.bool,
    /** Called when video is loaded and ready to play. */
    onReady: PropTypes.func,
    /** Called when video starts or resumes playing after pausing or buffering. */
    onPlay: PropTypes.func,
    /** Called when the video is paused. */
    onPause: PropTypes.func,
    /** Called when video starts buffering. */
    onBuffer: PropTypes.func,
    /** Called when video has finished buffering. */
    onBufferEnd: PropTypes.func,
    /** Called when an error occurs whilst attempting to play media. */
    onError: PropTypes.func,
    /** Called when the first frame of the video is available. */
    onLoadedData: PropTypes.func,
    /** Callback containing `played` and `loaded` progress as a fraction, and `playedSeconds` and `loadedSeconds` in seconds.*/
    onProgress: PropTypes.func,
    /** Callback containing duration of the media, in seconds. */
    onDuration: PropTypes.func,
    /** Only supported when using mpegts.js. Callback invoked with response headers. */
    onHeaders: PropTypes.func,
    /** Which player implementation to use. Defaults to selecting based on URL (mpegts.js or ReactPlayer). Pass `'webrtc'` to use the WHEP player. */
    transport: PropTypes.oneOf(['webrtc']),
    /** Override ReactPlayer settings. See https://www.npmjs.com/package/react-player#config-prop. WebRTC options go under `config.webrtc`. */
    config: PropTypes.object,
    /** Optional overlay content to render on top of the video. */
    overlayContent: PropTypes.node,
    /** Width of player. */
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** Height of player. */
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** Use a dewarped view. */
    dewarpView: PropTypes.oneOf([VIEW_SINGLE, VIEW_TRIPLE, VIEW_QUADRANTS]),
    /** Fisheye camera position for dewarp view. */
    fisheyeOrientation: PropTypes.oneOf([
        ORIENTATION_CEILING,
        ORIENTATION_WALL,
        ORIENTATION_FLOOR,
    ]),
    /** Selected dewarp camera (for keyboard navigation). An outline will be drawn around it. */
    selectedDewarpCamera: PropTypes.number,
    /** Stretch ~8:9 sources to fill the 16:9 container instead of letterboxing. Default true. */
    stretchToContainer: PropTypes.bool,
};

VideoPlayer.displayName = 'VideoPlayer';

// Wrapper for mpegts.js
// Attempts to provide same interface as ReactPlayer
const MpegTsPlayer = memo(
    forwardRef(
        (
            {
                url,
                width = '100%',
                height = '100%',
                playing,
                volume,
                muted,
                playsinline,
                playbackRate,
                onReady,
                onPlay,
                onPause,
                onBuffer,
                onBufferEnd,
                onError,
                onLoadedData,
                onProgress,
                progressInterval,
                onDuration,
                onHeaders,
                config,
                ...props
            },
            ref
        ) => {
            // Reference to mpegts.js player instance
            const mpegtsRef = useRef();
            // Reference to video element
            const videoRef = useRef();

            // Stabilise relevant props that are set up when initialising player
            // We want the player to always use latest function passed in props but without having to rerun useEffect
            const onHeadersRef = useRef();
            onHeadersRef.current = onHeaders;
            const onErrorRef = useRef();
            onErrorRef.current = onError;
            const onProgressRef = useRef();
            onProgressRef.current = onProgress;
            const onLoadedDataRef = useRef();
            onLoadedDataRef.current = onLoadedData;
            const progressIntervalRef = useRef();
            progressIntervalRef.current = progressInterval;
            const onDurationRef = useRef();
            onDurationRef.current = onDuration;

            // Ref exposed by this component
            const thisRef = useMemo(() => {
                return {
                    isMpegts: true,
                    // Imitate ReactPlayer
                    getInternalPlayer: (key) =>
                        key === 'mpegts' ? mpegtsRef.current : videoRef.current,
                    getCurrentTime: () => mpegtsRef.current.currentTime,
                    getDuration: () => {
                        return isFinite(mpegtsRef.current.duration)
                            ? mpegtsRef.current.duration
                            : null;
                    },
                    seekTo: (amount, type) => {
                        if (!type) {
                            type =
                                amount >= 0 && amount <= 1
                                    ? 'fraction'
                                    : 'seconds';
                        }
                        if (type === 'seconds') {
                            mpegtsRef.current.currentTime = amount;
                        } else if (isFinite(mpegtsRef.current.duration)) {
                            mpegtsRef.current.currentTime =
                                amount * mpegtsRef.current.duration;
                        }
                    },
                    getSecondsLoaded: () => {
                        // `buffered` returns a new TimeRanges object on each access and the buffer can change between reads
                        try {
                            const buffered = mpegtsRef.current?.buffered;
                            if (buffered?.length > 0) {
                                return buffered.end(0);
                            }
                        } catch (error) {
                            // Buffer changed under us
                        }
                        return null;
                    },
                };
            }, []);

            useImperativeHandle(
                ref,
                () => {
                    return thisRef;
                },
                [thisRef]
            );

            // Set up mpegts.js
            useEffect(() => {
                // Create mpegts.js player instance
                let player;

                try {
                    player = mpegts.createPlayer(
                        {
                            type: 'mpegts',
                            url,
                        },
                        {
                            onHeaders: onHeadersRef.current,
                            // Lazy load aborts the request once (default) 3 minutes are buffered ahead and resumes with a range request
                            lazyLoad: false,
                        }
                    );

                    player.on(mpegts.Events.ERROR, (...args) => {
                        if (onErrorRef.current) {
                            onErrorRef.current(...args);
                        }
                    });

                    player.attachMediaElement(videoRef.current);
                    player.load();

                    mpegtsRef.current = player;
                    trackVideoPlayerCreated();
                } catch (error) {
                    // Sometimes mpegts.js throws errors
                    // e.g. `undefined is not a constructor (evaluating 'new self.MediaSource')` when invoking attachMediaElement
                    // Hasn't happened often enough to go to the effort of debugging mpegts.js code so just catching for now
                    if (onErrorRef.current) {
                        onErrorRef.current(error);
                    }
                }

                const video = videoRef.current;

                // Flag indicating whether `onDuration` prop callback has been invoked
                // We only do this once
                let onDurationCalled = false;

                // Last known values of seconds played and loaded
                let lastLoadedSeconds;
                let lastPlayedSeconds;
                // Timestamp of last time we invoked `onProgress` prop callback
                // We must not do this more often than `progressInterval` prop value if provided
                let lastOnProgressCall;
                // To ensure we do not invoked `onProgress` more often than `progressInterval`, we may
                // need to use a timeout. When one is active, its id is stored here.
                let onProgressTimeout;

                // Callback for when video loading and playing
                const onProgress = (event) => {
                    const video = event.target;
                    const duration = player.duration;

                    // If `onDuration` callback has been provided and we have not yet invoked it,
                    // do this now if we know the duration.
                    if (
                        !onDurationCalled &&
                        onDurationRef.current &&
                        isFinite(duration)
                    ) {
                        onDurationRef.current(duration);
                        onDurationCalled = true;
                    }

                    // Handle `onProgress` prop callback
                    // Read `buffered` once: each access returns a new TimeRanges object and (on iOS) the buffer can be
                    // discarded between reads, which makes `end(0)` throw an IndexSizeError
                    let loadedSeconds = null;
                    if (onProgressRef.current && isFinite(duration)) {
                        try {
                            const buffered = video.buffered;
                            if (buffered.length > 0) {
                                loadedSeconds = buffered.end(0);
                            }
                        } catch (error) {
                            loadedSeconds = null;
                        }
                    }
                    if (loadedSeconds !== null) {
                        const playedSeconds = video.currentTime;

                        // Only invoke callback if values have changed
                        if (
                            loadedSeconds !== lastLoadedSeconds ||
                            playedSeconds !== lastPlayedSeconds
                        ) {
                            lastLoadedSeconds = loadedSeconds;
                            lastPlayedSeconds = playedSeconds;

                            // When invoking the callback, update `lastOnProgressCall` and clear timeout if necessary
                            const invokeOnProgress = () => {
                                onProgressRef.current({
                                    playedSeconds: lastPlayedSeconds,
                                    played: lastPlayedSeconds / duration,
                                    loadedSeconds: lastLoadedSeconds,
                                    loaded: lastLoadedSeconds / duration,
                                });
                                lastOnProgressCall = Date.now();
                                onProgressTimeout = null;
                            };

                            // If there is already a timeout waiting, we don't need to do anything else
                            // This timeout will always pull the latest values
                            if (!onProgressTimeout) {
                                const progressInterval =
                                    progressIntervalRef.current;
                                if (
                                    typeof progressInterval === 'number' &&
                                    progressInterval > 0 &&
                                    lastOnProgressCall &&
                                    Date.now() - lastOnProgressCall <
                                        progressInterval
                                ) {
                                    // We last invoked the callback too recently, so create a timeout to run it again
                                    onProgressTimeout = setTimeout(
                                        invokeOnProgress,
                                        Date.now() - lastOnProgressCall
                                    );
                                } else {
                                    // Safe to invoke now
                                    invokeOnProgress();
                                }
                            }
                        }
                    }
                };
                video.addEventListener('progress', onProgress);
                video.addEventListener('timeupdate', onProgress);

                const handleLoadedData = (...args) => {
                    onLoadedDataRef.current?.(...args);
                };
                video.addEventListener('loadeddata', handleLoadedData);

                return () => {
                    // Tear down in the order mpegts.js expects. Skipping any of these steps
                    // (or reordering them) can leave the MediaSource / SourceBuffer in an open
                    // state while segments are still being appended, which surfaces as a
                    // DOMException "The object is in an invalid state" (code 11) MSE error.
                    // Each step is guarded individually because a failure partway through
                    // shutdown must not prevent the remaining steps from running.
                    if (player) {
                        try {
                            player.pause();
                        } catch (e) {
                            /* ignore */
                        }
                        try {
                            player.unload();
                        } catch (e) {
                            /* ignore */
                        }
                        try {
                            player.detachMediaElement();
                        } catch (e) {
                            /* ignore */
                        }
                        try {
                            player.destroy();
                        } catch (e) {
                            /* ignore */
                        }
                        trackVideoPlayerDestroyed();
                    }
                    // Release the media element's decoder and buffers now. Without this iOS can hold on to them until the element
                    // is garbage collected, and creating and destroying players quickly (e.g. repeated seeks) runs out of memory.
                    try {
                        video.removeAttribute('src');
                        video.load();
                    } catch (e) {
                        /* ignore */
                    }
                    mpegtsRef.current = null;
                    video.removeEventListener('progress', onProgress);
                    video.removeEventListener('timeupdate', onProgress);
                    video.removeEventListener('loadeddata', handleLoadedData);
                    clearTimeout(onProgressTimeout);
                };
            }, [url]);

            // Set up listeners for ReactPlayer-style callbacks
            useEffect(() => {
                if (onReady) {
                    const invokeOnReady = () => {
                        onReady(thisRef);
                    };

                    const video = videoRef.current;
                    video.addEventListener('canplay', invokeOnReady);

                    return () => {
                        video.removeEventListener('canplay', invokeOnReady);
                    };
                }
            }, [onReady, thisRef]);
            useEffect(() => {
                if (onPlay) {
                    const video = videoRef.current;
                    video.addEventListener('play', onPlay);

                    return () => {
                        video.removeEventListener('play', onPlay);
                    };
                }
            }, [onPlay]);
            useEffect(() => {
                if (onPause) {
                    const video = videoRef.current;
                    video.addEventListener('pause', onPause);

                    return () => {
                        video.removeEventListener('pause', onPause);
                    };
                }
            }, [onPause]);
            useEffect(() => {
                if (onBuffer) {
                    const video = videoRef.current;
                    video.addEventListener('waiting', onBuffer);

                    return () => {
                        video.removeEventListener('waiting', onBuffer);
                    };
                }
            }, [onBuffer]);
            useEffect(() => {
                if (onBufferEnd) {
                    const video = videoRef.current;
                    video.addEventListener('playing', onBufferEnd);

                    return () => {
                        video.removeEventListener('playing', onBufferEnd);
                    };
                }
            }, [onBufferEnd]);

            // Ensure video plays and pauses when `playing` prop changes
            useEffect(() => {
                if (playing) {
                    mpegtsRef.current?.play();
                } else {
                    mpegtsRef.current?.pause();
                }
            }, [playing]);

            // Update video playback rate when prop changes
            useEffect(() => {
                if (playbackRate) {
                    videoRef.current.playbackRate = playbackRate;
                }
            }, [playbackRate]);

            // Seems to be a bug in React where muted attribute doesn't get applied to DOM element
            // This then sometimes mean browsers refuse to autoplay video
            // This useEffect just adds muted attribute manually each time the chunk changes
            useEffect(() => {
                if (!muted) {
                    videoRef.current.removeAttribute('muted');
                } else {
                    videoRef.current.setAttribute('muted', 'muted');
                }
            }, [muted]);

            return (
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <video
                    ref={videoRef}
                    width={width}
                    height={height}
                    autoPlay={playing}
                    muted={muted}
                    playsInline={playsinline}
                    // Follow same `config` object style as ReactPlayer
                    {...(config?.file?.attributes ?? {})}
                    {...props}
                />
            );
        }
    )
);

// Video player with our custom controls
// Reset component when URL changes
export const VideoPlayerWithControls = memo(
    forwardRef((props, ref) => (
        <VideoPlayerWithControlsRenderer key={props.url} ref={ref} {...props} />
    ))
);
const VideoPlayerWithControlsRenderer = withTranslation(null, {
    withRef: true,
})(
    memo(
        forwardRef(
            (
                {
                    autoPlay = false,
                    initialVolume,
                    initialMuted = false,
                    initialPlaybackRate = 1,
                    onReady: onReadyProp,
                    onProgress: onProgressProp,
                    onDuration: onDurationProp,
                    width = '100%',
                    height = '100%',
                    zoomable = false,
                    style,
                    t,
                    tReady,
                    i18n,
                    ...props
                },
                ref
            ) => {
                const containerRef = useRef();
                const playerRef = useRef();

                // Element around video which key and touch listeners are added to
                const keyboardAndTouchInterceptorRef = useRef();

                // Is video currently playing?
                const [playing, setPlaying] = useState(false);
                // Volume between 0 and 1, or null for default
                // Don't currently have UI control for volume
                const [volume] = useState(initialVolume);
                // Is video muted?
                const [muted, setMuted] = useState(initialMuted);
                // Playback rate as number
                // Don't currently have UI control for playback rate
                const [playbackRate] = useState(initialPlaybackRate);
                // Is user currently seeking?
                const [seeking, setSeeking] = useState(false);
                // Which mode are we showing?
                const [mode, setMode] = useState('default');

                const zoomControlsRef = useRef();
                const zoomDisabled = !zoomable || mode !== 'default';
                useKeyboardZoomControls(keyboardAndTouchInterceptorRef, {
                    zoomControlsRef,
                    playerRef,
                    dewarpView: mode === 'dewarp' ? VIEW_SINGLE : undefined,
                    disabled: mode === 'default' && !zoomable,
                });

                // Reset zoom when switching to dewarp
                useEffect(() => {
                    if (mode === 'dewarp') {
                        zoomControlsRef.current.resetTransform();
                    }
                }, [mode]);

                useImperativeHandle(
                    ref,
                    () => {
                        return {
                            container: containerRef.current,
                            player: playerRef.current.player,
                            reactPlayer: playerRef.current.reactPlayer,
                            mpegts: playerRef.current.mpegts,
                            webrtc: playerRef.current.webrtc,
                            getInternalPlayer:
                                playerRef.current.getInternalPlayer,
                            pause: () => setPlaying(false),
                            takeSnapshot: () => {
                                playerRef.current.showSnapshotAnimation();
                                if (playerRef.current.getDewarpedCanvas()) {
                                    return playerRef.current.getDewarpedCanvas();
                                } else {
                                    const canvas = getMediaSnapshot(
                                        playerRef.current.getInternalPlayer(),
                                        containerRef.current,
                                        zoomControlsRef.current.instance
                                            .transformState
                                    );
                                    return canvas;
                                }
                            },
                        };
                    },
                    []
                );

                /*
            Video info identified when video starts loading.
            This needs to be a ref as it is used in event listeners.

            Shape:
            {
                durationSeconds: integer,
                hasAudio: boolean
            }
        */
                const videoInfoRef = useRef({
                    durationSeconds: null,
                    hasAudio: null,
                });
                // Have we obtained the video info?
                const [gotVideoInfo, setGotVideoInfo] = useState();
                // Update video info
                const onDuration = useCallback(
                    (durationSeconds) => {
                        videoInfoRef.current = {
                            durationSeconds,
                            hasAudio: doesVideoHaveAudio(
                                playerRef.current.getInternalPlayer()
                            ),
                        };
                        setGotVideoInfo(true);

                        onDurationProp?.(durationSeconds);
                    },
                    [onDurationProp]
                );

                /*
            Playing/loading progress.
            This needs to be a ref as it is used in event listeners.

            Shape:
            {
                playedSeconds: integer,
                loadedSeconds: integer
            }
        */
                const progressInfoRef = useRef({
                    playedSeconds: null,
                    loadedSeconds: null,
                });
                /*
            State used for scrub bar.
            Generated from refs above.

            Shape: {
                value: number,
                max: number,
                percentagePlayed: number,
                percentageLoaded: number,
                currentTimestamp: string,
                endTimestamp: string
            }
        */
                const [scrubBarData, setScrubBarData] = useState({
                    value: 0,
                    max: 0,
                    percentagePlayed: 0,
                    percentageLoaded: 0,
                    currentTimestamp: getTimestamp(0),
                    endTimestamp: null,
                });
                // This must be invoked whenever `progressInfoRef` changes
                const updateScrubBarData = useCallback(() => {
                    // We set scrub bar range <input> to a fixed time step (e.g. 100ms)
                    // Need to make duration, played time and loaded time all line up with that step interval
                    // (otherwise we'd get issues like scrub handle never reaching the end or not lining up with track gradient)

                    const roundUpToNearestStep = (value) =>
                        value
                            ? Math.ceil(value / SCRUB_BAR_STEP) * SCRUB_BAR_STEP
                            : 0;

                    const durationSeconds = roundUpToNearestStep(
                        videoInfoRef.current.durationSeconds
                    );
                    const playedSeconds = roundUpToNearestStep(
                        progressInfoRef.current.playedSeconds
                    );
                    const loadedSeconds = roundUpToNearestStep(
                        progressInfoRef.current.loadedSeconds
                    );

                    setScrubBarData({
                        value: playedSeconds,
                        max: durationSeconds,
                        percentagePlayed:
                            (playedSeconds / durationSeconds) * 100 || 0,
                        percentageLoaded:
                            (loadedSeconds / durationSeconds) * 100 || 0,
                        currentTimestamp: getTimestamp(
                            progressInfoRef.current.playedSeconds ?? 0
                        ),
                        endTimestamp: videoInfoRef.current.durationSeconds
                            ? getTimestamp(videoInfoRef.current.durationSeconds)
                            : null,
                    });
                }, []);
                // Callback to update `progressInfoRef`
                const onProgress = useCallback(
                    (progress) => {
                        progressInfoRef.current = {
                            playedSeconds: progress.playedSeconds,
                            loadedSeconds: progress.loadedSeconds,
                        };
                        updateScrubBarData();

                        if (progress.played === 1) {
                            // Reset `playing` when video reaches end
                            setPlaying(false);
                        }

                        onProgressProp?.(progress);
                    },
                    [updateScrubBarData, onProgressProp]
                );

                /*
            If autoPlay is requested, we attempt to play the video
            when onReady is invoked. We can't just set initial
            value of `playing` to true as browser may not actually
            autoplay video.
        */
                const initialAutoPlayRef = useRef({
                    // Lock initial value of `autoPlay` prop in here
                    initialProp: autoPlay,
                    // Have we attempted to autoplay yet?
                    // We only attempt the very first time.
                    handled: false,
                });
                const onReady = useCallback(
                    (player) => {
                        if (
                            initialAutoPlayRef.current.initialProp &&
                            !initialAutoPlayRef.current.handled
                        ) {
                            const attemptAutoPlay = async () => {
                                initialAutoPlayRef.current.handled = true;
                                try {
                                    // This promise will resolve if successful
                                    await player.getInternalPlayer().play();
                                    setPlaying(true);
                                } catch (error) {
                                    console.error(error);
                                }
                            };
                            attemptAutoPlay();
                        }

                        onReadyProp?.(player);
                    },
                    [onReadyProp]
                );

                // For keyboard users, if any video control is in focus, we need to show UI
                // This state stores the video element currently in focus (or null if none are)
                const [focusedVideoElement, setFocusedVideoElement] =
                    useState();
                // onFocus handler for any video element
                const onVideoElementFocus = useCallback((event) => {
                    setFocusedVideoElement(event.target);
                }, []);
                // onBlur handler for any video element
                const onVideoElementBlur = useCallback((event) => {
                    setFocusedVideoElement((element) =>
                        element === event.target ? null : element
                    );
                }, []);
                // Only interfere with UI behaviour if this is true
                const { isFocusVisible } = useFocusVisible();

                // Seek to specific time in video
                const seek = useCallback(
                    (seconds) => {
                        playerRef.current.getInternalPlayer().currentTime =
                            seconds;
                        progressInfoRef.current.playedSeconds = seconds;
                        updateScrubBarData();
                    },
                    [updateScrubBarData]
                );

                // Can make container fullscreen
                const [toggleFullscreen, isFullcreen, fullscreenEnabled] =
                    useFullscreen(containerRef);
                // When a mouse user is in fullscreen, UI (including cursor) should hide after a few seconds of inactivity
                // so as not to cover the video
                const [hideUIInFullscreen, setHideUIInFullscreen] =
                    useState(false);
                // Set up mouse listeners when in fullscreen to show/hide UI
                useEffect(() => {
                    // We want to hide UI after a few seconds of inactivity whilst video is playing
                    // Also don't want to hide it if user is holding down on scrub bar for a long time
                    if (isFullcreen && isUsingMouse() && playing && !seeking) {
                        // Timeout to hide UI
                        let timeout;

                        // Function to clear existing timeout and start a new one
                        const restartTimeout = () => {
                            clearTimeout(timeout);
                            timeout = setTimeout(() => {
                                setHideUIInFullscreen(true);
                            }, 3000);
                        };

                        // On each mouse action, we want to make sure UI is showing and restart timeout to hide it again
                        const showUIAndRestartTimeout = () => {
                            setHideUIInFullscreen(false);
                            restartTimeout();
                        };

                        // Set initial timeout
                        restartTimeout();

                        const container = containerRef.current;

                        // Listeners for each mouse action
                        const onMouseDownHandler = (event) => {
                            showUIAndRestartTimeout();
                        };
                        const onMouseUpHandler = (event) => {
                            showUIAndRestartTimeout();
                        };
                        const onMouseMoveHandler = (event) => {
                            showUIAndRestartTimeout();
                        };

                        container.addEventListener(
                            'mousedown',
                            onMouseDownHandler
                        );
                        container.addEventListener('mouseup', onMouseUpHandler);
                        container.addEventListener(
                            'mousemove',
                            onMouseMoveHandler
                        );

                        return () => {
                            clearTimeout(timeout);
                            container.removeEventListener(
                                'mousedown',
                                onMouseDownHandler
                            );
                            container.removeEventListener(
                                'mouseup',
                                onMouseUpHandler
                            );
                            container.removeEventListener(
                                'mousemove',
                                onMouseMoveHandler
                            );
                        };
                    } else {
                        // Reset this state whenever not in use
                        setHideUIInFullscreen(false);
                    }
                }, [isFullcreen, playing, seeking]);

                // Play/pause video
                const togglePlaying = useCallback(() => {
                    // If video is at the end, restart it
                    if (
                        progressInfoRef.current.playedSeconds ===
                        videoInfoRef.current.durationSeconds
                    ) {
                        seek(0);
                    }

                    setPlaying((currentlyPlaying) => !currentlyPlaying);
                }, [seek]);

                // On touch screens, we want to show/hide controls when user touches video
                // This state is a boolean indicating whether we should currently show UI because video was touched recently
                const [showControlsForTouch, setShowControlsForTouch] =
                    useState(false);
                // When UI is activated by a touch, we set a timeout to hide it again
                // This is the id of the current timeout
                const touchTimeoutRef = useRef();
                // This function should be called when any UI control element is actived (e.g. button clicked, scrub bar moved)
                // It restarts the current timeout
                const onControlElementTouched = useCallback(() => {
                    if (isUsingTouchscreen()) {
                        clearTimeout(touchTimeoutRef.current);
                        touchTimeoutRef.current = setTimeout(() => {
                            setShowControlsForTouch(false);
                        }, 3000);
                        setShowControlsForTouch(true);
                    }
                }, []);
                // Make sure no timeout is left running when component unmounts
                useEffect(() => {
                    return () => {
                        clearTimeout(touchTimeoutRef.current);
                    };
                }, []);

                /*
            Set up event listeners to achieve the following:
                * Pause and play video with mouse click or spacebar
                * Toggle controls UI showing on touch
        */
                useEffect(() => {
                    const keyboardAndTouchInterceptor =
                        keyboardAndTouchInterceptorRef.current;

                    // Mouse handling

                    // Position of mouse when click started
                    let mouseDownX, mouseDownY;
                    const onMouseDownHandler = (event) => {
                        // Ignore click events on touch devices
                        // We don't want touches to play/pause video (touches bring up UI instead)
                        // Also make sure to exclude clicks on zoom control buttons
                        if (
                            isUsingMouse() &&
                            event.target.tagName !== 'BUTTON'
                        ) {
                            mouseDownX = event.clientX;
                            mouseDownY = event.clientY;
                        }
                    };
                    const onMouseUpHandler = (event) => {
                        // Only take action if mouse didn't move during click, otherwise this was a drag (probably for zoom controls)
                        if (
                            typeof mouseDownX === 'number' &&
                            typeof mouseDownY === 'number'
                        ) {
                            // Use Pythagoras to get distance between click start and end
                            const distance = Math.sqrt(
                                Math.pow(event.clientX - mouseDownX, 2) +
                                    Math.pow(event.clientY - mouseDownY, 2)
                            );
                            mouseDownX = null;
                            mouseDownY = null;
                            if (distance < 1) {
                                togglePlaying();
                            }
                        }
                    };

                    // Spacebar handling
                    const keyHandler = (event) => {
                        switch (event.key) {
                            case ' ':
                                togglePlaying();
                                event.preventDefault();
                                return;
                            default:
                                return;
                        }
                    };

                    // Touch handling
                    // Only show UI for touches not drags (so not to interfere with dewarping controls)
                    let touchStartX, touchStartY;
                    const onTouchStart = (event) => {
                        touchStartX = event.touches[0].clientX;
                        touchStartY = event.touches[0].clientY;
                    };
                    const onTouchEnd = (event) => {
                        if (
                            typeof touchStartX === 'number' &&
                            typeof touchStartY === 'number' &&
                            Math.hypot(
                                event.changedTouches[0].clientX - touchStartX,
                                event.changedTouches[0].clientY - touchStartY
                            ) < 3
                        ) {
                            clearTimeout(touchTimeoutRef.current);

                            setShowControlsForTouch((currentlyShowing) => {
                                if (currentlyShowing) {
                                    // If currently showing, now hide
                                    return false;
                                } else {
                                    // If currently hidden, start showing
                                    // Start timeout to hide again
                                    touchTimeoutRef.current = setTimeout(() => {
                                        setShowControlsForTouch(false);
                                    }, 3000);
                                    return true;
                                }
                            });
                        }

                        // Reset
                        touchStartX = null;
                        touchStartY = null;
                    };

                    keyboardAndTouchInterceptor.addEventListener(
                        'mousedown',
                        onMouseDownHandler
                    );
                    keyboardAndTouchInterceptor.addEventListener(
                        'mouseup',
                        onMouseUpHandler
                    );
                    keyboardAndTouchInterceptor.addEventListener(
                        'keydown',
                        keyHandler
                    );
                    keyboardAndTouchInterceptor.addEventListener(
                        'touchstart',
                        onTouchStart
                    );
                    keyboardAndTouchInterceptor.addEventListener(
                        'touchend',
                        onTouchEnd
                    );

                    return () => {
                        keyboardAndTouchInterceptor.removeEventListener(
                            'mousedown',
                            onMouseDownHandler
                        );
                        keyboardAndTouchInterceptor.removeEventListener(
                            'mouseup',
                            onMouseUpHandler
                        );
                        keyboardAndTouchInterceptor.removeEventListener(
                            'keydown',
                            keyHandler
                        );
                        keyboardAndTouchInterceptor.removeEventListener(
                            'touchstart',
                            onTouchStart
                        );
                        keyboardAndTouchInterceptor.removeEventListener(
                            'touchend',
                            onTouchEnd
                        );
                    };
                }, [togglePlaying]);

                return (
                    <VideoPlayerWithControlsContainer
                        ref={containerRef}
                        style={_.merge(style ?? {}, { width, height })}
                        $hideCursor={hideUIInFullscreen}
                    >
                        <VideoKeyboardAndTouchInterceptor
                            ref={keyboardAndTouchInterceptorRef}
                            tabIndex={0}
                        >
                            <ZoomableWrapper
                                ref={zoomControlsRef}
                                disabled={zoomDisabled}
                            >
                                <ZoomableContent>
                                    <VideoPlayer
                                        ref={playerRef}
                                        playing={playing && !seeking}
                                        volume={volume}
                                        muted={muted}
                                        playsInline={autoPlay}
                                        playbackRate={playbackRate}
                                        onReady={onReady}
                                        onProgress={onProgress}
                                        onDuration={onDuration}
                                        progressInterval={250}
                                        onFocus={onVideoElementFocus}
                                        onBlur={onVideoElementBlur}
                                        dewarpView={
                                            mode === 'dewarp'
                                                ? VIEW_SINGLE
                                                : undefined
                                        }
                                        {...props}
                                    />
                                </ZoomableContent>

                                {!zoomDisabled && <ZoomControls />}
                            </ZoomableWrapper>
                        </VideoKeyboardAndTouchInterceptor>

                        <VideoControlBar
                            onTouchEnd={() => onControlElementTouched()}
                            $playing={playing}
                            $controlsFocused={
                                isFocusVisible && !!focusedVideoElement
                            }
                            $touchActive={showControlsForTouch}
                            $hideUIInFullscreen={hideUIInFullscreen}
                            className="video-player-controls"
                        >
                            {/* Play button */}
                            <PlayButtonContainer>
                                <VideoControlButton
                                    aria-label={
                                        tReady
                                            ? t(
                                                  playing
                                                      ? translationKeys.video
                                                            .PAUSE
                                                      : translationKeys.video
                                                            .PLAY
                                              )
                                            : playing
                                            ? 'Pause'
                                            : 'Play'
                                    }
                                    onClick={() => {
                                        togglePlaying();
                                        onControlElementTouched();
                                    }}
                                    onFocus={onVideoElementFocus}
                                    onBlur={onVideoElementBlur}
                                >
                                    {playing ? <PauseIcon /> : <PlayIcon />}
                                </VideoControlButton>
                            </PlayButtonContainer>

                            {/* Scrub bar */}
                            <ScrubBarContainer>
                                <Span>{scrubBarData.currentTimestamp}</Span>

                                <Input
                                    style={{
                                        '--percentage-played': `${scrubBarData.percentagePlayed}%`,
                                        '--percentage-loaded': `${scrubBarData.percentageLoaded}%`,
                                    }}
                                    type="range"
                                    disabled={!gotVideoInfo}
                                    step={SCRUB_BAR_STEP}
                                    min={0}
                                    max={scrubBarData.max}
                                    value={scrubBarData.value}
                                    onChange={({ target: { value } }) => {
                                        seek(parseFloat(value));
                                        onControlElementTouched();
                                    }}
                                    // Need to stop playing whilst seeking
                                    // Shouldn't matter if user scrubs using keyboard
                                    onMouseDown={() => {
                                        // Ensure mouse and touch events don't both fire
                                        if (!isUsingTouchscreen()) {
                                            setSeeking(true);
                                        }
                                    }}
                                    onMouseUp={() => {
                                        // Ensure mouse and touch events don't both fire
                                        if (!isUsingTouchscreen()) {
                                            setSeeking(false);
                                        }
                                    }}
                                    // Are these needed?
                                    onTouchStart={() => {
                                        setSeeking(true);
                                    }}
                                    onTouchEnd={() => {
                                        setSeeking(false);
                                    }}
                                    onFocus={onVideoElementFocus}
                                    onBlur={onVideoElementBlur}
                                />

                                {scrubBarData.endTimestamp && (
                                    <Span>{scrubBarData.endTimestamp}</Span>
                                )}
                            </ScrubBarContainer>

                            {/* Volume button */}
                            <VolumeButtonContainer>
                                <VideoControlButton
                                    aria-label={
                                        tReady
                                            ? t(
                                                  muted
                                                      ? translationKeys.video
                                                            .audio.TURN_ON
                                                      : translationKeys.video
                                                            .audio.TURN_OFF
                                              )
                                            : muted
                                            ? 'Turn audio on'
                                            : 'Turn audio off'
                                    }
                                    onClick={() => {
                                        setMuted(
                                            (currentlyMuted) => !currentlyMuted
                                        );
                                        onControlElementTouched();
                                    }}
                                    disabled={
                                        !gotVideoInfo ||
                                        !videoInfoRef.current.hasAudio
                                    }
                                    onFocus={onVideoElementFocus}
                                    onBlur={onVideoElementBlur}
                                >
                                    {muted || volume === 0 ? (
                                        <AudioOffIcon />
                                    ) : (
                                        <AudioOnIcon />
                                    )}
                                </VideoControlButton>
                            </VolumeButtonContainer>

                            {/* Mode button */}
                            <ModeButtonContainer>
                                <VideoControlButton
                                    aria-label={t(
                                        mode === 'dewarp'
                                            ? translationKeys.video.dewarping
                                                  .STANDARD_VIEW
                                            : translationKeys.video.dewarping
                                                  .DEWARP_FISHEYE
                                    )}
                                    onClick={() => {
                                        setMode((currentMode) => {
                                            return currentMode === 'dewarp'
                                                ? 'default'
                                                : 'dewarp';
                                        });
                                        onControlElementTouched();
                                    }}
                                    onFocus={onVideoElementFocus}
                                    onBlur={onVideoElementBlur}
                                >
                                    {mode === 'default' ? (
                                        <DewarpedViewIcon />
                                    ) : (
                                        <StandardViewIcon />
                                    )}
                                </VideoControlButton>
                            </ModeButtonContainer>

                            {/* Fullscreen button */}
                            <FullscreenButtonContainer>
                                {fullscreenEnabled && (
                                    <VideoControlButton
                                        aria-label={
                                            tReady
                                                ? t(
                                                      isFullcreen
                                                          ? translationKeys
                                                                .actions
                                                                .EXIT_FULLSCREEN
                                                          : translationKeys
                                                                .video
                                                                .PLAY_FULLSCREEN
                                                  )
                                                : isFullcreen
                                                ? 'Exit fullscreen'
                                                : 'Play fullscreen'
                                        }
                                        onClick={() => {
                                            toggleFullscreen();
                                            onControlElementTouched();
                                        }}
                                        disabled={!gotVideoInfo}
                                        onFocus={onVideoElementFocus}
                                        onBlur={onVideoElementBlur}
                                    >
                                        {isFullcreen ? (
                                            <ShrinkIcon />
                                        ) : (
                                            <FullscreenIcon />
                                        )}
                                    </VideoControlButton>
                                )}
                            </FullscreenButtonContainer>
                        </VideoControlBar>
                    </VideoPlayerWithControlsContainer>
                );
            }
        )
    )
);
VideoPlayerWithControlsRenderer.displayName = 'VideoPlayerWithControlsRenderer';

VideoPlayerWithControls.propTypes = {
    /** URL of video. */
    url: PropTypes.string.isRequired,
    /** Attempt to play video immediately. */
    autoPlay: PropTypes.bool,
    /** Set the initial volume between 0 and 1. */
    initialVolume: PropTypes.number,
    /** Start with the player muted. */
    initialMuted: PropTypes.bool,
    /** Set the initial playback rate. */
    initialPlaybackRate: PropTypes.number,
    /** Called when video is loaded and ready to play. */
    onReady: PropTypes.func,
    /** Called when video starts or resumes playing after pausing or buffering. */
    onPlay: PropTypes.func,
    /** Called when the video is paused. */
    onPause: PropTypes.func,
    /** Called when video starts buffering. */
    onBuffer: PropTypes.func,
    /** Called when video has finished buffering. */
    onBufferEnd: PropTypes.func,
    /** Called when an error occurs whilst attempting to play media. */
    onError: PropTypes.func,
    /** Called when the first frame of the video is available. */
    onLoadedData: PropTypes.func,
    /** Callback containing `played` and `loaded` progress as a fraction, and `playedSeconds` and `loadedSeconds` in seconds.*/
    onProgress: PropTypes.func,
    /** Callback containing duration of the media, in seconds. */
    onDuration: PropTypes.func,
    /** Override ReactPlayer settings. See https://www.npmjs.com/package/react-player#config-prop. */
    config: PropTypes.object,
    /** Optional overlay content to render on top of the video. */
    overlayContent: PropTypes.node,
    /** Width of player. */
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** Height of player. */
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** Enable pan, pinch and zoom. */
    zoomable: PropTypes.bool,
};

VideoPlayerWithControls.displayName = 'VideoPlayerWithControls';

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

// This always rounds down to nearest whole second
const getTimestamp = (seconds) =>
    `${Math.floor(seconds / 60)}:${Math.floor(seconds % 60)
        .toString()
        .padStart(2, '0')}`;

// For MediaStream sources (WebRTC) audio tracks live on srcObject, not on the media element's
// mozHasAudio / webkitAudioDecodedByteCount / audioTracks properties.
const doesVideoHaveAudio = (videoPlayer) => {
    if (
        videoPlayer.srcObject &&
        typeof videoPlayer.srcObject.getAudioTracks === 'function'
    ) {
        return videoPlayer.srcObject.getAudioTracks().length > 0;
    }
    return (
        videoPlayer.mozHasAudio ||
        !!videoPlayer.webkitAudioDecodedByteCount ||
        (videoPlayer.audioTracks && videoPlayer.audioTracks.length > 0)
    );
};

export default VideoPlayer;
